import React, { useEffect, useRef, useState, useCallback } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Bell, Check, X, Stethoscope, ShoppingBag, Building2, HandHeart, Syringe, Calendar, Gift, ClipboardCheck, Scissors, Footprints } from "lucide-react";
import { API } from "../App";

// Map backend `icon` string → actual Lucide component
const ICONS = {
  Bell, Stethoscope, ShoppingBag, Building2, HandHeart, Syringe,
  Calendar, Gift, ClipboardCheck, Scissors, Footprints,
};

function timeAgo(iso) {
  if (!iso) return "";
  const t = new Date(iso).getTime();
  const diff = Math.max(0, Date.now() - t);
  const min = Math.floor(diff / 60000);
  if (min < 1) return "just now";
  if (min < 60) return `${min}m`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h`;
  const d = Math.floor(hr / 24);
  if (d < 7) return `${d}d`;
  return new Date(iso).toLocaleDateString();
}

export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(false);
  const lastSeenId = useRef(null);
  const navigate = useNavigate();
  const wrapRef = useRef(null);

  const fetchCount = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/me/notifications/unread-count`, { withCredentials: true });
      setUnread(r.data?.count || 0);
    } catch { /* silent — user might not be logged in */ }
  }, []);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/me/notifications`, {
        params: { limit: 20 }, withCredentials: true,
      });
      const next = r.data?.items || [];
      // Surface a sonner toast for any *new* unread item we haven't shown yet
      const newest = next.find(n => !n.read);
      if (newest && lastSeenId.current && newest.notification_id !== lastSeenId.current) {
        toast(newest.title, { description: newest.body, duration: 4500 });
      }
      if (newest) lastSeenId.current = newest.notification_id;
      setItems(next);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, []);

  // Poll the unread count every 30s; also refresh when the bell is opened
  useEffect(() => {
    fetchCount();
    const t = setInterval(fetchCount, 30000);
    return () => clearInterval(t);
  }, [fetchCount]);

  useEffect(() => {
    if (open) fetchList();
  }, [open, fetchList]);

  // Click-outside to close
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  async function markRead(id, e) {
    e?.stopPropagation();
    setItems(prev => prev.map(n => n.notification_id === id ? { ...n, read: true } : n));
    setUnread(c => Math.max(0, c - 1));
    try { await axios.post(`${API}/me/notifications/${id}/read`, {}, { withCredentials: true }); }
    catch { /* keep optimistic state */ }
  }

  async function markAllRead() {
    setItems(prev => prev.map(n => ({ ...n, read: true })));
    setUnread(0);
    try { await axios.post(`${API}/me/notifications/read-all`, {}, { withCredentials: true }); }
    catch { /* silent */ }
  }

  async function dismiss(id, e) {
    e?.stopPropagation();
    const was = items.find(n => n.notification_id === id);
    setItems(prev => prev.filter(n => n.notification_id !== id));
    if (was && !was.read) setUnread(c => Math.max(0, c - 1));
    try { await axios.delete(`${API}/me/notifications/${id}`, { withCredentials: true }); }
    catch { /* silent */ }
  }

  function openItem(n) {
    if (!n.read) markRead(n.notification_id);
    setOpen(false);
    if (n.link) navigate(n.link);
  }

  return (
    <div className="relative" ref={wrapRef}>
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        data-testid="notification-bell"
        className="relative p-2 rounded-full hover:bg-surface-muted transition-colors"
        aria-label={`Notifications${unread ? ` · ${unread} unread` : ""}`}
        title="Notifications"
      >
        <Bell className="w-4 h-4 text-ink-soft" />
        {unread > 0 && (
          <span
            data-testid="notification-unread-badge"
            className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] rounded-full bg-brand text-white text-[10px] font-bold grid place-items-center px-1 leading-none border border-bg"
          >
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          data-testid="notification-dropdown"
          className="absolute right-0 mt-2 w-[360px] max-w-[92vw] bg-surface border border-line rounded-2xl shadow-soft overflow-hidden z-50"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-line">
            <div className="font-heading text-sm font-extrabold">Notifications</div>
            {items.some(n => !n.read) && (
              <button
                type="button"
                onClick={markAllRead}
                data-testid="notification-mark-all-read"
                className="text-[11px] font-bold text-brand hover:underline inline-flex items-center gap-1"
              >
                <Check className="w-3 h-3"/>Mark all read
              </button>
            )}
          </div>

          <div className="max-h-[60vh] overflow-y-auto">
            {loading && items.length === 0 && (
              <div className="px-4 py-8 text-center text-xs text-ink-soft" data-testid="notification-loading">Loading…</div>
            )}
            {!loading && items.length === 0 && (
              <div className="px-4 py-10 text-center" data-testid="notification-empty">
                <Bell className="w-8 h-8 mx-auto text-ink-soft opacity-40 mb-2"/>
                <div className="text-sm font-bold">You&apos;re all caught up</div>
                <div className="text-[11px] text-ink-soft mt-1">New activity will show here.</div>
              </div>
            )}
            {items.map(n => {
              const Icon = ICONS[n.icon] || Bell;
              return (
                <div
                  key={n.notification_id}
                  data-testid={`notification-item-${n.notification_id}`}
                  onClick={() => openItem(n)}
                  className={`group flex gap-3 px-4 py-3 border-b border-line/60 last:border-0 cursor-pointer hover:bg-surface-muted transition-colors ${n.read ? "" : "bg-brand/[0.04]"}`}
                >
                  <div className={`shrink-0 w-9 h-9 rounded-full grid place-items-center ${n.read ? "bg-surface-muted text-ink-soft" : "bg-brand/15 text-brand"}`}>
                    <Icon className="w-4 h-4"/>
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-start justify-between gap-2">
                      <div className={`text-sm leading-snug ${n.read ? "font-medium text-ink-soft" : "font-bold text-ink"}`}>{n.title}</div>
                      <div className="shrink-0 text-[10px] text-ink-soft mt-0.5">{timeAgo(n.created_at)}</div>
                    </div>
                    {n.body && <div className="text-[12px] text-ink-soft mt-0.5 line-clamp-2">{n.body}</div>}
                  </div>
                  <div className="flex flex-col gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    {!n.read && (
                      <button
                        type="button"
                        onClick={(e) => markRead(n.notification_id, e)}
                        data-testid={`notification-read-${n.notification_id}`}
                        className="p-1 rounded-full hover:bg-bg text-ink-soft"
                        title="Mark read"
                      >
                        <Check className="w-3.5 h-3.5"/>
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={(e) => dismiss(n.notification_id, e)}
                      data-testid={`notification-dismiss-${n.notification_id}`}
                      className="p-1 rounded-full hover:bg-bg text-ink-soft"
                      title="Dismiss"
                    >
                      <X className="w-3.5 h-3.5"/>
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
